import { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import noop from 'lodash/noop';
import { isAxiosError, isCancel } from 'axios';
import { datadogRum } from '@datadog/browser-rum';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { tryReturnToToolPage } from '@/services/auth/standalone-tool-return';
import { TokenRefreshTimeoutError } from '@/services/auth/token-refresh';
import { StandalonePageLoader } from '@/components/Common/PageLoader/PageLoader';
import { ErrorPage } from '@/components/Errors/ErrorPage/ErrorPage';
import { I18N_KEYS } from '@/language/keys';
import { log } from '@/logger/logger.ts';
import useWorkspacesStore from '@/state/workspaces.state';
import { useUserStore } from '@/state/user.state';
import { useGlobalStore } from '@/state/global.state';
import { useFoldersStore } from '@/state/folders.state';
import { queryClient } from '@/providers/queryClient';
import { FOLDER_CONTENTS_QUERY_KEY } from '@/queries/useFolders';
import {
  clearTierCookie,
  detectApiOriginChallenge,
  initAxiosInterceptors,
  keepTierCookieForSurvivingSession,
  revokeSession,
  resumeTierCookie,
} from '@/services/axiosConfig';
import { useFirebaseAuthProvider } from '@/services/auth/firebase/useFirebaseAuthProvider';
import { useFigmaEmbedAuthProvider } from '@/services/auth/figma-embed/useFigmaEmbedAuthProvider';
import { getAuthErrorMessage } from '@/services/auth/auth-errors';
import {
  refreshDefaultHeaders,
  setDefaultHeaders,
  setProviderHeaders,
  unsetDefaultHeaders,
} from '@/services/auth/auth-headers';
import { clearTurnstileToken } from '@/services/auth/turnstile-token.service';
import { useAuthConfig } from '@/hooks/useAuthConfig';
import { AUTH_PROVIDERS, IDP_INITIATED_REDIRECT_PATH } from '@/consts/auth.consts';
import { getAppVersion, isVersionGreater as isMajorVersionChanged } from '@/utils/general';
import { INCOMING_HEADER_NAMES } from '@/consts/headers';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { SERVER_ERROR_CODES } from '@/consts/server-error-codes.consts';
import { RECIPE_FONTS_QUERY_KEY_ROOT } from '@/queries/useWorkflowFonts';
import { isCommunityPreviewOptionalAuth } from '@/utils/auth.utils';
import { sanitizeError } from '@/utils/sanitize-error';
import { clearOidcAccessToken } from '@/services/auth/firebase/oidc-providers/figma-oidc-provider';
import { WORKSPACE_FONTS_QUERY_KEY_ROOT } from '@/queries/useWorkspaceFonts';
import { FIGMA_CONNECTION_QUERY_KEY } from '@/hooks/useLinkedFigmaAccount';
import { getNewUserLanding, type NewUserLandingOutcome } from '@/utils/newUserLanding';
import { asyncNoop } from '../utils/functions';
import type { User, EnrichedUserFields, AuthUser } from '../types/auth.types';
import type { AuthProviderHook } from '@/services/auth/authProvider.types.ts';

const logger = log.getLogger('AuthContext');

/**
 * Whether a failed `/v1/users` enrichment says nothing about the session itself: the request got no
 * answer (network, timeout, a stalled token refresh), the server or edge was overloaded, or the edge
 * challenged it. These are worth retrying. A 401/403/404 is the server's verdict on the session or
 * account, so it still falls through to signed-out and the sign-in page.
 */
const isRetryableEnrichFailure = (error: unknown): boolean => {
  if (error instanceof TokenRefreshTimeoutError) {
    return true;
  }
  if (!isAxiosError(error) || isCancel(error)) {
    return false;
  }
  const { response } = error;
  if (!response) {
    return true;
  }
  return (
    response.status >= 500 ||
    response.status === 408 ||
    response.status === 429 ||
    detectApiOriginChallenge(response) !== null
  );
};

/**
 * Appends `?welcome=` params so the WelcomeModal dispatcher can pick the
 * highest-priority modal to show. Multiple codes may be added (e.g. both a
 * granted promotion and new_user); the dispatcher selects one by priority.
 */
function appendWelcomeParams(
  route: string,
  { grantedPromotion, isNewUser }: { grantedPromotion?: string; isNewUser: boolean },
): string {
  let result = route;
  if (grantedPromotion) {
    const sep = result.includes('?') ? '&' : '?';
    result = `${result}${sep}welcome=${encodeURIComponent(grantedPromotion)}`;
  }
  if (isNewUser) {
    const sep = result.includes('?') ? '&' : '?';
    result = `${result}${sep}welcome=new_user`;
  }
  return result;
}

interface SignedInUser {
  uid: AuthUser['uid'];
  displayName: AuthUser['displayName'];
  photoURL: AuthUser['photoURL'];
  email: AuthUser['email'];
  accessToken?: AuthUser['accessToken'];
}

export interface SignInOptions {
  hintEmail?: string | null;
  microsoftTenantId?: string;
  providerType: 'google' | 'microsoft' | 'figma';
  route: string;
  teamInviteToken?: string | null;
  promotion?: string | null;
  authorizationCode?: string | null;
  turnstileToken?: string;
  onSignInFlowFailed?: () => void;
}

export interface OnSignInSuccessParams {
  promotion: SignInOptions['promotion'];
  teamInviteToken?: SignInOptions['teamInviteToken'];
  route: SignInOptions['route'];
  additionalUserInfo: {
    isNewUser: boolean;
  } | null;
  user: SignedInUser;
  turnstileToken?: SignInOptions['turnstileToken'];
  onSignInFlowFailed?: SignInOptions['onSignInFlowFailed'];
}

interface AuthContextStore {
  currentUser: User | null;
  // True for a logged-out "Anyone with the link" Figma embed viewer.
  isAnonymousViewer: boolean;
  accessToken: string | null;
  isSigningIn: boolean;
  /**
   * A completed sign-in has issued its redirect but the router has not arrived yet. Sign-in surfaces
   * must not navigate while this is set, or they overwrite the destination with their own fallback.
   */
  isSignInRedirectPending: boolean;
  signIn: (options: SignInOptions) => Promise<void>;
  signOut: () => Promise<void>;
  initiateRedirectSignIn: (provider: string, redirectPath: string) => Promise<void>;
  getStoredPromotion: () => string | null;
  authApi: {
    getCurrentUser: () => AuthUser | null;
  };
  teamInviteSuccess: boolean;
  setTeamInviteSuccess: (success: boolean) => void;
  isMemberInviteTokenValid: boolean;
  setIsMemberInviteTokenValid: (isValid: boolean) => void;
  enrichUser: () => Promise<EnrichedUserFields | undefined>;
  onSignInSuccess: (params: OnSignInSuccessParams) => Promise<void>;
  onSignInError: (error: any) => void;
}

export const AuthContext = createContext<AuthContextStore>({
  currentUser: null,
  isAnonymousViewer: false,
  accessToken: null,
  isSigningIn: false,
  isSignInRedirectPending: false,
  signIn: asyncNoop,
  signOut: asyncNoop,
  initiateRedirectSignIn: asyncNoop,
  getStoredPromotion: () => null,
  authApi: {
    getCurrentUser: () => null,
  },
  teamInviteSuccess: false,
  setTeamInviteSuccess: noop,
  isMemberInviteTokenValid: false,
  setIsMemberInviteTokenValid: noop,
  enrichUser: () => Promise.resolve(undefined),
  onSignInSuccess: asyncNoop,
  onSignInError: noop,
});

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const authInfo = useAuthConfig();
  const isFigmaEmbed = authInfo.provider === AUTH_PROVIDERS.FIGMA_EMBED;

  // `key` is keyed to the resolved provider so a swap remounts the subtree. Without it React
  // reuses the same `AuthProviderInner` instance across providers, and the different
  // `useAuthHook` prop would change the internal hook graph — violating the Rules of Hooks.
  if (isFigmaEmbed) {
    return (
      <AuthProviderInner key={AUTH_PROVIDERS.FIGMA_EMBED} useAuthHook={useFigmaEmbedAuthProvider}>
        {children}
      </AuthProviderInner>
    );
  }

  return (
    <AuthProviderInner key={AUTH_PROVIDERS.FIREBASE} useAuthHook={useFirebaseAuthProvider}>
      {children}
    </AuthProviderInner>
  );
};

const AuthProviderInner = ({
  children,
  useAuthHook,
}: {
  children: React.ReactNode;
  useAuthHook: () => AuthProviderHook;
}) => {
  const [enrichedUserFields, setEnrichedUserFields] = useState<EnrichedUserFields | null>(null);
  const [loading, setLoading] = useState(true);
  const [teamInviteSuccess, setTeamInviteSuccess] = useState(false);
  const [isMemberInviteTokenValid, setIsMemberInviteTokenValid] = useState(false);
  // Ref is the synchronous re-entrancy guard; the state re-renders the loader once enrichment settles.
  const isEnrichingUserRef = useRef(false);
  const [isEnrichingUser, setIsEnrichingUser] = useState(false);
  // The last enrichment failed for a reason unrelated to the session (see isRetryableEnrichFailure).
  const [hasRetryableEnrichFailure, setHasRetryableEnrichFailure] = useState(false);
  const isSigningInRef = useRef(false);
  /**
   * Set between issuing the post-sign-in redirect and the router arriving. The loader below unmounts
   * the sign-in page mid-flight, and the copy that mounts behind it sees a signed-in user at a
   * location that is still `/signin` — without this it navigates to its own fallback and overwrites
   * the destination a Community visitor came for.
   */
  const isSignInRedirectPendingRef = useRef(false);
  const { provider } = useAuthConfig();
  const { t } = useTranslation();

  // Call the passed-in hook
  const authProvider = useAuthHook();
  const authUser = authProvider.user;
  const isAuthLoading = authProvider.isLoading;
  const [accessToken, setAccessToken] = useState(authProvider.accessToken);
  const { track } = useAnalytics();

  useEffect(() => {
    setAccessToken(authProvider.accessToken);
  }, [authProvider.accessToken]);

  const authApi = useMemo(
    () => ({
      getCurrentUser: () => authProvider.getCurrentUser(),
    }),
    [authProvider],
  );
  const updateActiveWorkspace = useWorkspacesStore((state) => state.updateActiveWorkspace);
  const initWorkspaces = useWorkspacesStore((state) => state.initWorkspaces);
  const setUser = useUserStore((state) => state.setUser);
  const updateSnackbarData = useGlobalStore((state) => state.updateSnackbarData);

  const currentUser = useMemo(() => {
    if (!enrichedUserFields || !authUser) return null;

    const merged = { ...authUser, ...enrichedUserFields };
    return {
      ...merged,
      uid: enrichedUserFields.id, // Use the DB-saved id, which may differ from the auth provider's uid
    };
  }, [enrichedUserFields, authUser]);

  // handle identity provider user state changes
  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (authUser) {
      setDefaultHeaders(authUser.accessToken);
      // Inject the auth handle before the restored-session enrichUser GET below; the other injection
      // site (AxiosConfiguration) is a descendant and mounts only after this GET has dispatched.
      initAxiosInterceptors(authApi);
      // A logged-out "Anyone with the link" embed viewer has no Figma profile to
      // enrich (their `currentUser` stays null by design). Skip the doomed
      // /v1/users call so they don't pay a blocking full-screen spinner before
      // the logged-out embed chrome can render.
      if (!authUser.isAnonymous) {
        void enrichUser();
      }
    } else {
      setEnrichedUserFields(null);
      setHasRetryableEnrichFailure(false);
      unsetDefaultHeaders();
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, [authUser, isAuthLoading]);

  // handle enriched user state changes
  useEffect(() => {
    if (!currentUser) {
      datadogRum.clearUser();
      return;
    }

    updateActiveWorkspace({
      ...currentUser?.activeWorkspace,
    });
    if (currentUser?.workspaces) {
      initWorkspaces(currentUser?.workspaces);
    }

    setUser(currentUser);
    datadogRum.setUser({ id: currentUser.uid });
  }, [currentUser, initWorkspaces, updateActiveWorkspace, setUser]);

  // handle access token state changes
  useEffect(() => {
    if (accessToken) {
      setDefaultHeaders(accessToken);
    } else {
      setEnrichedUserFields(null);
      unsetDefaultHeaders();
    }
  }, [accessToken]);

  useEffect(() => {
    setProviderHeaders(isCommunityPreviewOptionalAuth() ? null : provider, getAppVersion());
  }, [provider]);

  const navigate = useNavigate();
  const location = useLocation();

  // Arriving anywhere releases the claim, so a later visit to a sign-in surface can navigate again.
  useEffect(() => {
    isSignInRedirectPendingRef.current = false;
  }, [location.key]);

  const signOut = useCallback(async () => {
    let sessionRevoked = false;
    try {
      // Complete authenticated cleanup before local sign-out removes the token.
      if (provider === AUTH_PROVIDERS.FIREBASE) {
        await revokeSession();
        sessionRevoked = true;
      } else {
        await clearTierCookie();
      }
      await authProvider.signOut();
      // Reset all stores
      queryClient.removeQueries({ queryKey: RECIPE_FONTS_QUERY_KEY_ROOT });
      queryClient.removeQueries({ queryKey: WORKSPACE_FONTS_QUERY_KEY_ROOT });
      // The query has a five-minute stale time and may contain a Figma email. Remove every variant
      // before another user can sign in within the same document and reuse the previous user's data.
      queryClient.removeQueries({ queryKey: [FIGMA_CONNECTION_QUERY_KEY] });
      useWorkspacesStore.getState().reset();
      useUserStore.getState().reset();
      useGlobalStore.getState().reset();
      useFoldersStore.getState().reset();
      // Clear cached folder data so a different user doesn't see stale folders after sign-in
      queryClient.removeQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY] });

      setEnrichedUserFields(null);
      setUser(null);
      setIsMemberInviteTokenValid(false);
    } catch (error) {
      // Do not restore credentials after successful server-side revocation.
      // KEEP_IN_SYNC with the tool page's `apps/src/sign-out.ts` (`signOutRevokingServerSession`).
      if (!sessionRevoked && authProvider.getCurrentUser()) {
        keepTierCookieForSurvivingSession();
      }
      logger.error('Error signing out:', sanitizeError(error));
      updateSnackbarData({ text: 'An error occurred while signing out.', isOpen: true, icon: null, severity: 'error' });
    }
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, [setUser, authProvider, provider]);

  const enrichUser = useCallback(async (fromSignIn = false): Promise<EnrichedUserFields | undefined> => {
    if (isEnrichingUserRef.current) return;
    if (isSigningInRef.current && !fromSignIn) return;
    // Set the sync guard and the render flag together so they can never drift apart.
    const setEnriching = (active: boolean) => {
      isEnrichingUserRef.current = active;
      setIsEnrichingUser(active);
    };
    setEnriching(true);
    try {
      const response = await safeAxios.get(API_ROUTES.users.root());

      // Check for app version updates (same mechanism as axios interceptor)
      const supportedAppVersion = response.headers[INCOMING_HEADER_NAMES.SUPPORTED_APP_VERSION] as string;
      const currentVersion = getAppVersion();
      if (supportedAppVersion && currentVersion) {
        if (isMajorVersionChanged(supportedAppVersion, currentVersion)) {
          logger.info(`New app version detected: ${supportedAppVersion} > ${currentVersion}. Setting refresh alert...`);
          useGlobalStore.getState().setIsShowRefreshAlert(true);
        }
      }

      const enrichedUser = response.data as EnrichedUserFields;
      setEnrichedUserFields(enrichedUser);
      setHasRetryableEnrichFailure(false);

      // Drain the Figma token this app used to persist in localStorage (see clearOidcAccessToken).
      clearOidcAccessToken();
      return enrichedUser;
    } catch (error) {
      // Sanitized: a raw AxiosError carries the request's bearer token in `config.headers`.
      logger.error('Error enriching user:', sanitizeError(error));
      setHasRetryableEnrichFailure(isRetryableEnrichFailure(error));
    } finally {
      setEnriching(false);
    }
  }, []);

  const sendDataToFirstPromoter = (email: string | null) => {
    if (email && window.fpr) {
      try {
        window.fpr('referral', { email });
      } catch (e) {
        logger.warn(`Failed to send data to FirstPromoter: ${String(e)}`);
      }
    }
  };

  const onSignInSuccess = useCallback(
    async ({
      user,
      promotion,
      teamInviteToken,
      route,
      additionalUserInfo: _additionalUserInfo,
      turnstileToken,
      onSignInFlowFailed,
    }: OnSignInSuccessParams) => {
      if (!user?.uid) {
        logger.error("signIn: uid doesn't exist");
      }

      try {
        let signInResponse: { isNewUser: boolean; grantedPromotion?: string };
        try {
          // A same-tab logout leaves credentials suspended, so resume must precede the POST for signin to
          // dispatch credentialed.
          resumeTierCookie();
          const response = await safeAxios.post<{ isNewUser: boolean; grantedPromotion?: string }>(
            API_ROUTES.users.signin(),
            {
              ...user,
              promotion,
              turnstileToken,
            },
            {
              withCredentials: true,
              skipTierBootstrap: true,
              // Pin axios-retry off: a response-less network error would otherwise re-POST the single-use
              // turnstileToken. (The interceptor's 401 replay is exempted separately, same reason.)
              'axios-retry': { retries: 0 },
            },
          );
          signInResponse = response.data;
        } finally {
          clearTurnstileToken();
        }

        if (teamInviteToken) {
          try {
            setDefaultHeaders(user.accessToken);
            // Through the shared instance, not bare axios: accepting a paid-workspace invite
            // recomputes the entitlement claim and stashes it for a same-response remint, which only
            // fires when the opt-in header rides along. Bare axios would drop the stash and let the
            // next bootstrap mint the pre-join tier from a lagging replica.
            await safeAxios.post(API_ROUTES.workspaces.invitationAccept({ token: teamInviteToken }));
            setTeamInviteSuccess(true);
          } catch (error: any) {
            logger.error('Failed accepting team invitation during sign in', error, {
              errorMessage: error?.message || 'N/A',
              userId: user?.uid,
              teamInviteToken,
            });

            if (
              error?.response?.data?.internalErrorCode === SERVER_ERROR_CODES.EMAIL_MISMATCH_ERROR_CODE &&
              error?.response?.data?.message
            ) {
              throw new Error(error.response.data.message);
            }

            throw error;
          }
        }

        await refreshDefaultHeaders(authProvider, user.accessToken);

        await enrichUser(true);
        let workflowRoute: string | null = null;
        if (signInResponse.isNewUser) {
          sendDataToFirstPromoter(user.email);
          track('user_signed_up_client', { source: 'client_app' }, TrackTypeEnum.Product);
          // Half of new users get a workflow to land in instead of the empty file browser. Only for
          // someone who did not ask for a page, a followed link still wins.
          let landingOutcome: NewUserLandingOutcome | 'followed_link' = 'followed_link';
          if (route === '/') {
            const landing = await getNewUserLanding();
            workflowRoute = landing.route;
            landingOutcome = landing.outcome;
          }
          // Also stored on the person, because the question we care about is what they do on later
          // days, and by then this event is long past. Set once, a person is only new one time.
          track(
            'new_user_landing',
            { outcome: landingOutcome, $set_once: { new_user_landing_group: landingOutcome } },
            TrackTypeEnum.Product,
          );
        }
        isSigningInRef.current = false;
        // `route` already carries it. Cleared only here so a failed attempt keeps it for the retry.
        sessionStorage.removeItem(IDP_INITIATED_REDIRECT_PATH);

        if (workflowRoute) {
          // A full page load, not a route change. The ?new=true that starts the onboarding is read
          // once when the page loads, so a route change would miss it. Opening a new file elsewhere
          // in the app loads the page the same way.
          window.location.href = appendWelcomeParams(workflowRoute, signInResponse);
          return;
        }
        const destination = appendWelcomeParams(route, signInResponse);
        // Claimed before navigating, because the navigation is what re-renders this provider and
        // remounts the sign-in page behind the loader.
        isSignInRedirectPendingRef.current = true;
        // A standalone tool page is a file on the server, so it needs a full page load. Anything
        // else, including a failure to get there, navigates in the app the way it always has.
        if (!tryReturnToToolPage(destination)) {
          navigate(destination);
        }
      } catch (error) {
        onSignInFlowFailed?.();
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
    [],
  );

  const onSignInError = useCallback((error: any) => {
    const errorMessage = getAuthErrorMessage(provider, error);
    logger.error(`error in signing in user: ${errorMessage}`);
    updateSnackbarData({ text: errorMessage, isOpen: true, icon: null, severity: 'error', duration: 10000 });
    setLoading(false);
    void authProvider.signOut();
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, []);

  const signIn = useCallback(
    async ({
      route,
      providerType,
      teamInviteToken,
      hintEmail,
      microsoftTenantId,
      promotion,
      authorizationCode,
      turnstileToken,
      onSignInFlowFailed,
    }: SignInOptions) => {
      if (!authProvider.signIn) {
        throw new Error('SignIn method is not supported for this provider');
      }
      isSigningInRef.current = true;
      try {
        const result = await authProvider.signIn(providerType, {
          hintEmail: hintEmail ?? undefined,
          microsoftTenantId,
          authorizationCode: authorizationCode ?? undefined,
        });

        const user = result.user;
        const additionalUserInfo = authProvider.getAdditionalUserInfo(result);

        await onSignInSuccess({
          user,
          promotion,
          route,
          additionalUserInfo,
          teamInviteToken,
          turnstileToken,
          onSignInFlowFailed,
        });
      } catch (error: any) {
        // Sign-in resumed credentials before the POST, so re-suspend and reset UI state first — ahead of
        // the message/logging/snackbar work, which must never be able to strand the user with live
        // credentials and a stuck loader.
        isSigningInRef.current = false;
        isSignInRedirectPendingRef.current = false;
        setLoading(false);
        void clearTierCookie().finally(() => {
          void authProvider.signOut();
        });
        const errorMessage = getAuthErrorMessage(provider, error);
        logger.error(`error in signing in user: ${errorMessage}`, error, hintEmail, error?.customData);
        updateSnackbarData({ text: errorMessage, isOpen: true, icon: null, severity: 'error', duration: 10000 });
        /** Keep the attempted promotion for retries after the OAuth handoff has been consumed. */
        const retryParams = new URLSearchParams();
        if (promotion) {
          retryParams.set('promotion', promotion);
        }
        navigate({ pathname: window.location.pathname, search: retryParams.toString() }, { replace: true });
      }
    },
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
    [enrichUser, navigate, authProvider.signOut, updateSnackbarData],
  );

  if (loading || isSigningInRef.current || (!currentUser && isEnrichingUser)) {
    return <StandalonePageLoader />;
  }

  // The session is still valid, so the sign-in page would only ask the user to redo a sign-in that
  // already succeeded. Standalone only: the Figma embed never routes to sign-in.
  if (provider === AUTH_PROVIDERS.FIREBASE && authUser && !currentUser && hasRetryableEnrichFailure) {
    return <ErrorPage actions={[{ label: t(I18N_KEYS.ERROR_PAGE.RETRY_ACTION), onClick: () => void enrichUser() }]} />;
  }

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        // Sourced from the raw auth user (the decoded embed token), NOT
        // `currentUser` — an anonymous viewer has no enriched fields, so
        // `currentUser` is null for them while `authUser` is still present.
        isAnonymousViewer: authUser?.isAnonymous ?? false,
        accessToken,
        authApi,
        initiateRedirectSignIn: authProvider.initiateRedirectSignIn || asyncNoop,
        getStoredPromotion: authProvider.getStoredPromotion,
        signIn,
        signOut,
        isSigningIn: isSigningInRef.current,
        isSignInRedirectPending: isSignInRedirectPendingRef.current,
        teamInviteSuccess,
        setTeamInviteSuccess,
        isMemberInviteTokenValid,
        setIsMemberInviteTokenValid,
        onSignInSuccess,
        onSignInError,
        enrichUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
